import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { ROUTES } from '@/config/constants';
import { Button, Spinner } from '@/components/ui';

/**
 * Turn the opaque PKCE failure into something actionable. The usual cause in dev
 * is being returned to a *different origin* than the one that started the flow
 * (Supabase falls back to its Site URL when the redirect origin isn't allow-
 * listed), so the code_verifier — scoped to the initiating origin's localStorage
 * — isn't here to finish the exchange.
 */
function explainExchangeError(message: string): string {
  if (/code verifier|invalid request|pkce|flow state/i.test(message)) {
    return (
      'Sign-in couldn’t be completed because you were returned to a different site than the one ' +
      `you started from. Add this exact URL (${window.location.origin}/auth/callback) to ` +
      'Supabase → Authentication → URL Configuration → Redirect URLs, and set the Site URL to ' +
      `where you’re testing. (${message})`
    );
  }
  return message;
}

/**
 * OAuth / magic-link / recovery landing page. Supabase redirects here with a
 * PKCE `?code=` (or an `?error=` / `?error_description=`). We exchange the code
 * for a session exactly once, then send the user into the app on a clean URL.
 *
 * This replaces the old behavior where the client relied solely on
 * `detectSessionInUrl` and a stray `?code=` could be left sitting on the public
 * landing page with no session ever established (the joinwinr.com/?code=… bug).
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // A PKCE code is single-use; guard against React StrictMode's double effect.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const oauthError = params.get('error_description') ?? params.get('error');
    const next = params.get('next') || ROUTES.home;

    void (async () => {
      if (oauthError) {
        setError(decodeURIComponent(oauthError));
        return;
      }
      if (!code) {
        // Nothing to exchange — just leave the callback route.
        navigate(next, { replace: true });
        return;
      }
      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      if (exchangeError) {
        setError(explainExchangeError(exchangeError.message));
        return;
      }
      // Session is set; AuthProvider's onAuthStateChange hydrates the profile.
      navigate(next, { replace: true });
    })();
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-lg font-semibold">Couldn’t finish sign-in</h1>
        <p className="max-w-sm text-sm opacity-70">{error}</p>
        <Button onClick={() => navigate(ROUTES.signIn, { replace: true })}>Back to sign in</Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner />
    </div>
  );
}
